import { MODES, isMode, modeOf, DEFAULT_MODE } from './themes.js';

export const RACE_MS = 15_000;
export const COUNTDOWN_MS = 3_000;
export const GRACE_MS = 450;
/** How long the result stays up before the room returns itself to the lobby for the next heat. */
export const RESULTS_MS = 15_000;
export const MAX_TAPS_PER_SECOND = 24;
export const TEAMS = MODES[DEFAULT_MODE].teams;
export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
export function batchInterval(count) { return clamp(Math.ceil(count / 600 * 20) * 50, 200, 850); }

/** Pure, deterministic authority. All scores are accepted raw taps, never rubber-banded. */
export class Game {
  constructor(saved, maxPlayers = 500) {
    this.maxPlayers = maxPlayers;
    this.mode = modeOf(saved?.mode);
    this.modeDefaultVersion = saved?.modeDefaultVersion || 0;
    this.players = new Map(saved?.players || []);
    this.phase = saved?.phase || 'lobby';
    this.round = saved?.round || 1;
    this.totals = saved?.totals || [0, 0];
    this.auto = saved?.auto ?? true;
    this.joinDelay = saved?.joinDelay || 25_000;
    this.autoStartAt = saved?.autoStartAt || 0;
    this.startAt = saved?.startAt || 0;
    this.endAt = saved?.endAt || 0;
    this.winner = saved?.winner ?? null;
    this.notice = saved?.notice || '';
    this.order = saved?.order || 0;
    // True while no host control room is open. Only the server sets it, from live connections; never persisted.
    this.unattended = false;
  }
  setMode(mode, now) {
    if (this.phase !== 'lobby' || !isMode(mode)) return false;
    if (mode !== this.mode) { this.mode = mode; this.autoStartAt = 0; this.arm(now); }
    return true;
  }
  /** Manual control only applies while a host is watching; an unattended room always runs itself. */
  autoRun() { return this.auto || this.unattended; }
  setUnattended(unattended, now) { if (unattended === this.unattended) return false; this.unattended = unattended; this.arm(now); return true; }
  /** When the result gives way to the next lobby, or 0 while a host holds it. */
  nextAt() { return this.phase === 'results' && this.autoRun() ? this.endAt + GRACE_MS + RESULTS_MS : 0; }
  connected() { return [...this.players.values()].filter(p => p.connected); }
  counts() { const n = [0, 0]; for (const p of this.players.values()) if (p.connected) n[p.team]++; return n; }
  rebalance() {
    if (this.phase !== 'lobby') return;
    this.connected().sort((a, b) => a.order - b.order).forEach((p, i) => { p.team = i % 2; p.playing = false; });
  }
  arm(now) {
    if (this.phase !== 'lobby') return;
    if (!this.autoRun() || this.connected().length < 2) this.autoStartAt = 0;
    else if (!this.autoStartAt) this.autoStartAt = now + this.joinDelay;
  }
  join(id, now) {
    let p = this.players.get(id);
    if (!p?.connected && this.connected().length >= this.maxPlayers) throw new Error('This heat is full. Please try again after the race.');
    if (!p) {
      for (const [key, old] of this.players) if (!old.connected && (this.phase === 'lobby' || !old.playing)) this.players.delete(key);
      const n = this.counts();
      p = { team: n[0] <= n[1] ? 0 : 1, connected: true, playing: false, score: 0, seen: 0, seq: -1, bucket: 6, bucketAt: now, order: ++this.order, lastSeen: now };
      this.players.set(id, p);
    }
    p.connected = true; p.lastSeen = now;
    this.rebalance(); this.arm(now);
    return p;
  }
  leave(id, now) {
    const p = this.players.get(id); if (p) { p.connected = false; p.lastSeen = now; }
    this.rebalance(); this.arm(now);
  }
  start(now) {
    if (this.phase !== 'lobby' || this.connected().length < 2) return false;
    this.rebalance(); this.phase = 'countdown'; this.startAt = now + COUNTDOWN_MS;
    this.endAt = this.startAt + RACE_MS; this.autoStartAt = 0; this.totals = [0, 0]; this.winner = null; this.notice = '';
    for (const p of this.players.values()) Object.assign(p, { playing: p.connected, score: 0, seen: 0, seq: -1, bucket: 6, bucketAt: this.startAt });
    return true;
  }
  next(now, notice = '') {
    this.round++; this.phase = 'lobby'; this.startAt = 0; this.endAt = 0; this.autoStartAt = 0;
    this.totals = [0, 0]; this.winner = null; this.notice = notice;
    for (const [id, p] of this.players) {
      if (!p.connected) this.players.delete(id);
      else Object.assign(p, { playing: false, score: 0, seen: 0, seq: -1 });
    }
    this.rebalance(); this.arm(now);
  }
  advance(now) {
    const before = this.phase, nextAt = this.nextAt();
    if (nextAt && now >= nextAt) this.next(now);
    if (this.phase === 'lobby' && this.autoStartAt && now >= this.autoStartAt) this.start(now);
    if (this.phase === 'countdown' && now >= this.startAt) this.phase = 'racing';
    if (this.phase === 'racing' && now >= this.endAt) this.phase = 'settling';
    if (this.phase === 'settling' && now >= this.endAt + GRACE_MS) {
      this.phase = 'results';
      this.winner = this.totals[0] === this.totals[1] ? null : +(this.totals[1] > this.totals[0]);
    }
    return before !== this.phase;
  }
  tap(id, msg, now) {
    this.advance(now);
    const p = this.players.get(id);
    if (!p || !p.connected || !p.playing || !['racing', 'settling'].includes(this.phase)) return 0;
    if (msg.round !== this.round || !Number.isSafeInteger(msg.seq) || msg.seq <= p.seq || msg.seq > 1e7 || !Number.isSafeInteger(msg.total) || msg.total < p.seen || msg.total > 100_000) return 0;
    if (!Number.isFinite(msg.at) || msg.at < this.startAt || msg.at >= this.endAt || msg.at > now + 100 || now < this.startAt || now > this.endAt + GRACE_MS) return 0;
    const delta = msg.total - p.seen;
    p.seen = msg.total; p.seq = msg.seq;
    const t = Math.min(now, this.endAt);
    // Bank enough tokens for an 850 ms batch without penalizing high attendance.
    p.bucket = Math.min(32, p.bucket + Math.max(0, t - p.bucketAt) / 1000 * MAX_TAPS_PER_SECOND);
    p.bucketAt = t;
    const remaining = Math.max(0, Math.floor((t - this.startAt) / 1000 * MAX_TAPS_PER_SECOND + 6) - p.score);
    const accepted = Math.min(delta, Math.floor(p.bucket), remaining);
    p.bucket -= accepted; p.score += accepted; p.lastSeen = now; this.totals[p.team] += accepted;
    return accepted;
  }
  you(id) {
    const p = this.players.get(id);
    return p ? { team: p.team, score: p.score, seen: p.seen, seq: p.seq, playing: p.playing, number: p.order } : null;
  }
  snapshot(now) {
    const counts = this.counts(); const roster = [0, 0];
    for (const p of this.players.values()) if (p.playing) roster[p.team]++;
    return { mode: this.mode, phase: this.phase, round: this.round, totals: this.totals, counts, roster, startAt: this.startAt, endAt: this.endAt, autoStartAt: this.autoStartAt, nextAt: this.nextAt(), auto: this.auto, joinDelay: this.joinDelay, winner: this.winner, notice: this.notice, batchMs: batchInterval(counts[0] + counts[1]), serverNow: now };
  }
  serialize() { const { unattended, ...rest } = this; return { ...rest, players: [...this.players] }; }
}

/** Presentation only. Reserve the final 1.6 yards for the verified photo finish. */
export function raceTargets(s, now) {
  const u = clamp((now - s.startAt) / RACE_MS, 0, 1);
  const lead = Math.max(...s.totals);
  if (!lead) return [0, 0];
  return s.totals.map(score => score === 0 ? 0 : 38.4 * u * (0.78 + 0.22 * score / lead));
}
